import React, { Suspense, useMemo } from "react";
import { Outlet } from "react-router-dom";
import { useSelector } from 'react-redux';
import { DashboardHeader } from '../../../shell/header/DashboardHeader.jsx';
import { DashboardSidebar } from "../../../shell/sidebar/DashboardSidebar.jsx";
import LoadingPage from "../../error-pages/LoadingPage.jsx";
import { useNavScope } from '../../../shell/sidebar/useNavScope.js';
import { useScopedNavLinks } from '../../../shell/sidebar/useScopedNavLinks.jsx';
import { NavTreeContext } from '../../../shell/sidebar/NavTreeContext.js';
import ChunkErrorBoundary from '../../../components/ChunkErrorBoundary.jsx';
import { PageHeader } from '../../../components/page-header/PageHeader.jsx';
import SectionHeader from '../../../components/section-header/SectionHeader.jsx';
import useWorkspaceBootstrap from '../../../shell/layouts/useWorkspaceBootstrap.js';
import WorkspaceLoadError from '../../../shell/layouts/WorkspaceLoadError.jsx';
import './Cheats.css';

/**
 * Wrapper for every Developer Tools page. Renders the page-level
 * PageHeader plus, optionally, the shared SectionHeader band so each
 * tool reads with the same hierarchy as the rest of the app (Remote
 * Config, AB Testing, Segmentation, …).
 *
 * <p>Section band is opt-in by passing an {@code icon}. The existing
 * {@code subtitle} prop (previously discarded) is repurposed as the
 * section tagline so most callers just need to add the icon — their
 * subtitle line gets surfaced automatically.</p>
 *
 * @param {string}    title           page-level title (PageHeader)
 * @param {string}    pagePath        permission lookup key
 * @param {IconDef}   icon            optional — when set, renders SectionHeader
 * @param {string}    subtitle        optional — used as SectionHeader tagline
 * @param {string}    sectionTitle    optional — overrides the section title (defaults to {@code title})
 * @param {ReactNode} sectionActions  optional — right-aligned slot in the section header
 */
export function DevToolsPage({
    title,
    pagePath,
    icon,
    subtitle,
    sectionTitle,
    sectionActions,
    actions,
    tabs,
    activeTab,
    onTabChange,
    children,
}) {
    return (
        <>
            <PageHeader
                title={title}
                pagePath={pagePath}
                actions={actions}
                tabs={tabs}
                activeTab={activeTab}
                onTabChange={onTabChange}
            />
            <div className="content-body">
                {icon && (
                    <SectionHeader
                        icon={icon}
                        title={sectionTitle || title}
                        tagline={subtitle}
                        actions={sectionActions}
                    />
                )}
                {children}
            </div>
        </>
    );
}

export default function Cheats() {
    const effectivePermissions = useSelector(state => state.effectivePermissions);

    // Hydrate session on direct load / refresh of a cheats page. The cheats route tree has its own
    // shell (not DashboardLayout), so without this a refresh leaves superUser + permissions unset
    // (both are intentionally non-persisted) — which would empty the nav, deny pages, and bounce a
    // no-org super-user to onboarding. Same load as DashboardLayout's (useWorkspaceBootstrap), retried,
    // and shown as an error card when it cannot be loaded; skips if already loaded at login.
    const workspace = useWorkspaceBootstrap({ loaded: !!effectivePermissions });

    // The Developer Tools scope's own links, like DashboardLayout. Handing the sidebar the whole tree with no scope
    // drew the member hub here (Homepage, Components, Games, Portfolio) - pages a Super User cannot open.
    const scope = useNavScope();
    const filteredLinks = useMemo(() => (effectivePermissions ? scope.links : []), [scope, effectivePermissions]);
    // Shared with the page headers, as in DashboardLayout, so their section switcher lists the tools.
    const navTree = useScopedNavLinks(filteredLinks, scope);

    return (
        <NavTreeContext.Provider value={navTree}>
        <div className="main">
            <DashboardSidebar navLinks={filteredLinks} scope={scope} />
            <div className="main-container">
                <DashboardHeader />
                {/* The flex wrapper is what gives `.content` its full width. `.main-container`
                    is a `flex column; align-items: center`, so a bare `.content` (which has no
                    explicit width) collapses to its max-content — squeezing every Dev Tools page
                    into a narrow centered column. DashboardLayout wraps `.content` in this same
                    `display: flex; width: 100%` row so `flex: 1` stretches it; mirror it here. */}
                <div className="ai-chatbot-content-wrapper">
                    <div className="content">
                        <ChunkErrorBoundary>
                            <Suspense fallback={<LoadingPage />}>
                                {!effectivePermissions && workspace.failed
                                    ? <WorkspaceLoadError onRetry={workspace.retry} />
                                    : <Outlet />}
                            </Suspense>
                        </ChunkErrorBoundary>
                    </div>
                </div>
            </div>
        </div>
        </NavTreeContext.Provider>
    );
}
